<template>
  <div class="dropdown"
       :class="{[customCss]: customCss, disabled: disabled, 'dropdown--dropup': isDropup}"
       v-click-outside="() => toggleDropdown(false)"
       v-on:click="() => toggleDropdown()"
  >
    <span class="toggle" v-tooltip="tooltip">
      <span class="label" :class="{[customLabelCss]: customLabelCss}">
        <i v-if="icon" :class="icon"/>
        <span>{{label}}</span>
      </span>
    </span>
    <ul class="menu" :class="{ 'menu--with-icons': hasIconOptions }" v-if="isDropdownOpen">
      <slot name="header"></slot>
      <li v-for="opt in options" :key="opt.label" :class="{ disabled: opt.disabled }">
        <span class="block" v-on:click="(e) => onOptionClick(e, opt)">
          <i v-if="opt.icon" :class="opt.icon" aria-hidden="true"></i>
          <span>{{ opt.label }}</span>
        </span>
      </li>
      <slot></slot>
    </ul>
  </div>
</template>

<script>
  import _ from 'lodash';

  export default {
    props: {
      customCss: {
        type: String,
        required: false
      },
      customLabelCss: {
        type: String,
        required: false
      },
      label: {
        type: String,
        required: false
      },
      options: {
        type: Array,
        required: true
      },
      disabledProp: {
        type: Boolean,
        required: false,
        default: false
      },
      icon: {
        type: String,
        required: false
      },
      tooltip: {
        type: String
      }
    },
    computed: {
      disabled () {
        return (this.options && this.options.length === 0) || this.disabledProp;
      },
      hasIconOptions() {
        return this.options.some((opt) => Boolean(opt.icon));
      },
    },
    methods: {
      estimateMenuHeight () {
        const optionCount = (this.options && this.options.length) || 0;
        return (optionCount * 36) + 16;
      },
      getClipRect () {
        const viewport = {
          top: 0,
          bottom: window.innerHeight,
        };
        let node = this.$el && this.$el.parentElement;

        while (node && node !== document.body) {
          const style = window.getComputedStyle(node);
          const overflowY = style.overflowY;
          const overflowX = style.overflowX;
          const canClip = overflowY === 'auto'
            || overflowY === 'scroll'
            || overflowY === 'hidden'
            || overflowX === 'auto'
            || overflowX === 'scroll'
            || overflowX === 'hidden';

          if (canClip) {
            const rect = node.getBoundingClientRect();
            return {
              top: Math.max(viewport.top, rect.top),
              bottom: Math.min(viewport.bottom, rect.bottom),
            };
          }

          node = node.parentElement;
        }

        return viewport;
      },
      shouldDropup (menuHeight) {
        if (!this.$el) {
          return false;
        }

        const triggerRect = this.$el.getBoundingClientRect();
        const clipRect = this.getClipRect();
        const gutter = 8;
        const spaceBelow = clipRect.bottom - triggerRect.bottom;
        const spaceAbove = triggerRect.top - clipRect.top;

        return spaceBelow < (menuHeight + gutter) && spaceAbove > spaceBelow;
      },
      updateMenuPlacement () {
        if (!this.isDropdownOpen) {
          this.isDropup = false;
          return;
        }

        this.$nextTick(() => {
          if (!this.isDropdownOpen || !this.$el) {
            return;
          }

          const menu = this.$el.querySelector('.menu');
          const menuHeight = menu ? menu.offsetHeight : this.estimateMenuHeight();
          this.isDropup = this.shouldDropup(menuHeight);
        });
      },
      toggleDropdown (isOpen) {
        if (this.disabled && isOpen !== false) {
          return;
        }

        if(_.isUndefined(isOpen)) {
          isOpen = !this.isDropdownOpen;
        }

        if (isOpen) {
          this.isDropup = this.shouldDropup(this.estimateMenuHeight());
          this.isDropdownOpen = true;
          this.$emit('open');
          this.updateMenuPlacement();
        } else {
          this.isDropdownOpen = false;
          this.isDropup = false;
        }
      },

      onOptionClick (e, option) {
        if(e) {
          e.preventDefault();
          e.stopPropagation();
        }

        this.toggleDropdown(false);
        this.$emit(option.action, e)
      },

      close () {
        this.toggleDropdown(false);
      }
    },
    data () {
      return {
        isDropdownOpen: false,
        isDropup: false
      }
    }
  }
</script>

<style lang="scss" scoped>
  .dropdown {
    &.disabled {
      .toggle  {
        .label:after {
          color: #999 !important;
          cursor: not-allowed;
          pointer-events: none;
        }
      }
    }

    .toggle {
      display: inline-block;
      border: none;
      background: transparent;
      white-space: nowrap;
      position: relative;

      .label:after {
        border: none!important;
        position: absolute;
        top: calc(50% - 5px);
        left: calc(50% - 6px);
        width: 10px;
        height: 10px;
        padding-right: 10px;
        display: inline-block;
        font: normal normal normal 14px/1 FontAwesome;
        font-size: inherit;
        text-rendering: auto;
        -webkit-font-smoothing: antialiased;
        content: "\F078";
        color: $white;
        font-size: 10px !important;
      }
    }

    &.dropdown--dropup .menu {
      top: auto;
      bottom: 100%;
      margin-top: 0 !important;
      margin-bottom: 5px;
    }

    .menu {
      top: 100%;
      margin-top: 5px!important;
      position: absolute;
      min-width: 120px;
      background-color: #fff;
      border-radius: 4px!important;
      border: 1px solid #e0e0e0;
      background-clip: padding-box;
      -webkit-overflow-scrolling: touch;
      box-shadow: 0 1px 3px rgba(0,0,0,.2);
      -webkit-backface-visibility: hidden;
      backface-visibility: hidden;

      &.menu--with-icons {
        text-align: left;

        li .block {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 0.75rem;
          text-align: left;
          cursor: pointer;

          > i {
            width: 1rem;
            flex-shrink: 0;
            text-align: center;
          }
        }
      }

      .disabled {
        background-color: #eaeaea;
      }

      li:hover {
        background-color: #e6f2ff;
      }
    }
  }
</style>